import {
  Button,
  Table,
  type TableProps,
} from '@omnipresentgroup/design-system';
import { useInvoiceReportEmployees } from '@omnipresentgroup/domains';
import type {
  InvoiceReport,
  InvoiceReportEmployee,
} from '@omnipresentgroup/types';
import {
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { INVOICE_EMPLOYEE_DETAILS_PATH } from '../../constants';
import { WrappedMixpanel } from '../../mixpanelWrapper';
import { useInvoiceReportEmployeesTableConfig } from './hooks';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 0;

const EMPTY_ARRAY: InvoiceReportEmployee[] = [];
const LOADING_ARRAY = Array.from(
  { length: DEFAULT_PAGE_SIZE },
  () => ({}) as InvoiceReportEmployee,
);

type Props = {
  invoice: InvoiceReport;
  companyId: string;
  userId: string;
};

export const InvoiceReportEmployeeList = ({
  companyId,
  invoice,
  userId,
}: Props) => {
  const navigate = useNavigate();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const {
    data: invoiceReportEmployeesResult,
    isLoading,
    isError,
    refetch,
  } = useInvoiceReportEmployees({
    page: pageIndex + 1,
    size: pageSize,
    invoiceId: invoice.id,
    companyId: companyId,
    enabled: !!companyId && !!invoice.id,
  });

  const { columns } = useInvoiceReportEmployeesTableConfig();

  useEffect(() => {
    refetch();
  }, [pageIndex, pageSize, refetch]);

  const paginationProps: TableProps['paginationProps'] = useMemo(() => {
    return {
      pageSize,
      pageIndex: pageIndex,
      canNextPage: (invoiceReportEmployeesResult?.pages ?? 0) > pageIndex + 1,
      canPreviousPage: pageIndex > DEFAULT_PAGE_INDEX,
      pageCount: invoiceReportEmployeesResult?.totalCount
        ? Math.ceil(
            Math.max(invoiceReportEmployeesResult.totalCount / pageSize, 1),
          )
        : DEFAULT_PAGE_INDEX,
      totalCount: isLoading
        ? pageSize
        : invoiceReportEmployeesResult?.totalCount,
    };
  }, [
    pageSize,
    pageIndex,
    invoiceReportEmployeesResult?.pages,
    invoiceReportEmployeesResult?.totalCount,
    isLoading,
  ]);

  const tableOptions: Omit<
    TableOptions<InvoiceReportEmployee>,
    'data' | 'columns'
  > = {
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
  };

  const table = useReactTable({
    data: isLoading
      ? LOADING_ARRAY
      : (invoiceReportEmployeesResult?.data ?? EMPTY_ARRAY),
    columns: columns,
    ...tableOptions,
  });

  return (
    <Table
      id="invoice-reports-list"
      table={table}
      loading={isLoading}
      errorStateProps={{
        id: 'invoice-reports-list-error',
        title: ERROR_STATE_TITLE,
        description: ERROR_STATE_DESCRIPTION,
        callToAction: <Button onClick={window.location.reload}>Retry</Button>,
      }}
      showErrorState={isError}
      usePagination
      showHeader={false}
      paginationProps={paginationProps}
      onRowClick={(row: InvoiceReportEmployee) => {
        WrappedMixpanel.track('Employee Clicked', {
          companyId,
          userId,
          invoiceId: invoice.id,
          memo: invoice.reference,
        });
        navigate(`${INVOICE_EMPLOYEE_DETAILS_PATH(invoice.id, row.id)}`);
      }}
    />
  );
};
